import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tab,
  Tabs,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Snackbar,
  Collapse,
  Tooltip
} from '@mui/material';
import {
  Refresh,
  PlayArrow,
  Edit,
  Delete,
  Schedule,
  History,
  Settings,
  CloudSync,
  CheckCircle,
  Error,
  Warning,
  Timer,
  Visibility,
  ExpandMore,
  ExpandLess,
  Terminal,
  ContentCopy
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import {
  EpicorConnectDashboard,
  SyncJob,
  SyncJobType,
  SyncJobStatus,
  SyncJobExecutionLog,
  SyncJobExecution,
  SYNC_JOB_TYPE_LABELS,
  SYNC_JOB_STATUS_LABELS,
  SYNC_JOB_STATUS_COLORS
} from '../../types/epicorConnect';
import { epicorConnectService } from '../../services/epicorConnectService';
import { useBackofficeAuth } from '../../hooks/useBackofficeAuth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EpicorConnectPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useBackofficeAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<EpicorConnectDashboard | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [executingJobs, setExecutingJobs] = useState<Set<string>>(new Set());
  // Which executions have their captured sync-tool output expanded. Logs
  // run to ~20k chars, so they stay collapsed until asked for.
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const toggleLogs = useCallback((executionId: string) => {
    setExpandedLogs(prev => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  }, []);
  
  // Auto-refresh functionality
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Execution logs dialog
  const [logsDialog, setLogsDialog] = useState<{ open: boolean; jobId: string | null; jobName: string }>({
    open: false,
    jobId: null,
    jobName: ''
  });
  const [executionLogs, setExecutionLogs] = useState<SyncJobExecutionLog | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadDashboard();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
      setError(t('backoffice.epicorConnect.errors.pleaseLogin'));
    }
  }, [authLoading, isAuthenticated]);

  // Auto-refresh effect
  useEffect(() => {
    if (autoRefresh && isAuthenticated) {
      const interval = setInterval(() => {
        loadDashboard(false); // Silent refresh
        setLastRefresh(new Date());
      }, 30000); // Refresh every 30 seconds
      
      setRefreshInterval(interval);
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [autoRefresh, isAuthenticated]);

  const loadDashboard = useCallback(async (showLoading: boolean = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);
      const dashboardData = await epicorConnectService.getDashboard();
      setDashboard(dashboardData);
      if (!showLoading && autoRefresh) {
        setSnackbarMessage('Dashboard refreshed automatically');
      }
    } catch (err: any) {
      setError(err.message || t('backoffice.epicorConnect.errors.failedToLoadDashboard'));
      console.error('Dashboard loading error:', err);
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [autoRefresh, t]);

  const handleManualRefresh = () => {
    loadDashboard(true);
    setSnackbarMessage('Dashboard refreshed manually');
  };

  const loadExecutionLogs = async (jobId: string) => {
    try {
      setLogsLoading(true);
      const logs = await epicorConnectService.getSyncJobExecutions(jobId, 1, 10);
      setExecutionLogs(logs);
    } catch (err: any) {
      console.error('Failed to load execution logs:', err);
      setSnackbarMessage('Failed to load execution logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleExecuteJob = async (jobId: string) => {
    try {
      setExecutingJobs(prev => new Set(prev).add(jobId));
      await epicorConnectService.executeSyncJob(jobId, {});
      
      // Refresh dashboard after a short delay to see updated status
      setTimeout(() => {
        loadDashboard();
      }, 1000);
      
    } catch (err: any) {
      setError(t('backoffice.epicorConnect.errors.failedToExecuteJob', { message: err.message }));
    } finally {
      setExecutingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const getConnectionStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'success';
      case 'disconnected': return 'error';
      case 'error': return 'error';
      default: return 'warning';
    }
  };

  const getConnectionStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle />;
      case 'disconnected': return <Error />;
      case 'error': return <Error />;
      default: return <Warning />;
    }
  };

  if (loading || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box p={3}>
        <Alert severity="error">{error || t('backoffice.epicorConnect.errors.failedToLoadDashboard')}</Alert>
        <Button onClick={() => loadDashboard(true)} variant="contained" sx={{ mt: 2 }}>
          {t('backoffice.epicorConnect.actions.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          {t('backoffice.epicorConnect.title')}
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          {/* Auto-refresh controls */}
          <Box display="flex" alignItems="center" gap={1}>
            <Timer fontSize="small" color={autoRefresh ? "primary" : "disabled"} />
            <FormControlLabel
              control={
                <Switch
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  size="small"
                />
              }
              label="Auto-refresh"
              sx={{ m: 0, fontSize: '0.875rem' }}
            />
            {autoRefresh && (
              <Typography variant="caption" color="text.secondary">
                Last: {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}
          </Box>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={handleManualRefresh}
            disabled={loading}
          >
            {t('backoffice.epicorConnect.actions.refresh')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Environment Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6" gutterBottom>
                {dashboard.epicor_environment.environment_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboard.epicor_environment.base_url}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.environment.database')}: {dashboard.epicor_environment.database_company} | 
                {t('backoffice.epicorConnect.environment.apiVersion')}: {dashboard.epicor_environment.api_version}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Chip
                icon={getConnectionStatusIcon(dashboard.epicor_environment.connection_status)}
                label={dashboard.epicor_environment.connection_status}
                color={getConnectionStatusColor(dashboard.epicor_environment.connection_status) as any}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {dashboard.total_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.totalJobs')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main">
                {dashboard.active_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.activeJobs')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main">
                {dashboard.scheduled_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.scheduledJobs')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main">
                {dashboard.running_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.runningJobs')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main">
                {dashboard.failed_jobs}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.failedJobs')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="text.secondary">
                {dashboard.last_24h_executions}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('backoffice.epicorConnect.stats.last24hRuns')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different views */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label={t('backoffice.epicorConnect.tabs.overview')} />
            <Tab label={t('backoffice.epicorConnect.tabs.syncJobs')} />
            <Tab label={t('backoffice.epicorConnect.tabs.executionHistory')} />
            <Tab label={t('backoffice.epicorConnect.tabs.settings')} />
          </Tabs>
        </Box>

        {/* Overview Tab */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {dashboard.sync_jobs.map((job) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={job.job_id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {job.job_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {SYNC_JOB_TYPE_LABELS[job.job_type]}
                        </Typography>
                      </Box>
                      <Chip
                        label={SYNC_JOB_STATUS_LABELS[job.status]}
                        size="small"
                        sx={{
                          backgroundColor: SYNC_JOB_STATUS_COLORS[job.status],
                          color: 'white'
                        }}
                      />
                    </Box>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('backoffice.epicorConnect.jobDetails.lastRun')}:
                      </Typography>
                      <Typography variant="body2">
                        {epicorConnectService.getLastRunDisplay(job.last_run_at)}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('backoffice.epicorConnect.jobDetails.nextRun')}:
                      </Typography>
                      <Typography variant="body2">
                        {epicorConnectService.getNextRunDisplay(job.next_run_at)}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('backoffice.epicorConnect.jobDetails.recordsProcessed')}:
                      </Typography>
                      <Typography variant="body2">
                        {job.records_processed.toLocaleString()}
                      </Typography>
                    </Box>
                    
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={
                          executingJobs.has(job.job_id) ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <PlayArrow />
                          )
                        }
                        disabled={job.is_running || executingJobs.has(job.job_id)}
                        onClick={() => handleExecuteJob(job.job_id)}
                      >
                        {job.is_running ? t('backoffice.epicorConnect.status.running') : t('backoffice.epicorConnect.actions.execute')}
                      </Button>
                      <IconButton 
                        size="small" 
                        title={t('backoffice.epicorConnect.actions.viewHistory')}
                        onClick={() => {
                          setLogsDialog({
                            open: true,
                            jobId: job.job_id,
                            jobName: job.job_name
                          });
                          loadExecutionLogs(job.job_id);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton size="small" title={t('backoffice.epicorConnect.actions.schedule')}>
                        <Schedule />
                      </IconButton>
                      <IconButton size="small" title={t('backoffice.epicorConnect.actions.settings')}>
                        <Settings />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        {/* Sync Jobs Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              {t('backoffice.epicorConnect.syncJobsManagement.title')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<CloudSync />}
              onClick={() => {/* TODO: Open create job dialog */}}
            >
              {t('backoffice.epicorConnect.actions.createSyncJob')}
            </Button>
          </Box>

          <Grid container spacing={3}>
            {dashboard.sync_jobs.map((job) => (
              <Grid size={{ xs: 12 }} key={job.job_id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                      <Box flex={1}>
                        <Typography variant="h6" gutterBottom>
                          {job.job_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('backoffice.epicorConnect.jobDetails.type')}: {SYNC_JOB_TYPE_LABELS[job.job_type]} | 
                          {t('backoffice.epicorConnect.jobDetails.status')}: {SYNC_JOB_STATUS_LABELS[job.status]}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                          label={SYNC_JOB_STATUS_LABELS[job.status]}
                          size="small"
                          sx={{
                            backgroundColor: SYNC_JOB_STATUS_COLORS[job.status],
                            color: 'white'
                          }}
                        />
                        {job.is_running && (
                          <Chip
                            label={t('backoffice.epicorConnect.status.running')}
                            size="small"
                            color="warning"
                            icon={<CircularProgress size={12} />}
                          />
                        )}
                      </Box>
                    </Box>

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('backoffice.epicorConnect.jobDetails.lastRun')}
                        </Typography>
                        <Typography variant="body2">
                          {epicorConnectService.getLastRunDisplay(job.last_run_at)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('backoffice.epicorConnect.jobDetails.nextRun')}
                        </Typography>
                        <Typography variant="body2">
                          {epicorConnectService.getNextRunDisplay(job.next_run_at)}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('backoffice.epicorConnect.jobDetails.recordsProcessed')}
                        </Typography>
                        <Typography variant="body2">
                          {job.records_processed.toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {t('backoffice.epicorConnect.jobDetails.successRate')}
                        </Typography>
                        <Typography variant="body2" color={job.success_rate >= 90 ? 'success.main' : 'warning.main'}>
                          {epicorConnectService.formatSuccessRate(job.success_rate)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" gap={1}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            executingJobs.has(job.job_id) ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : (
                              <PlayArrow />
                            )
                          }
                          disabled={job.is_running || executingJobs.has(job.job_id)}
                          onClick={() => handleExecuteJob(job.job_id)}
                        >
                          {job.is_running ? t('backoffice.epicorConnect.status.running') : t('backoffice.epicorConnect.actions.executeNow')}
                        </Button>
                        
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => {
                            setLogsDialog({
                              open: true,
                              jobId: job.job_id,
                              jobName: job.job_name
                            });
                            loadExecutionLogs(job.job_id);
                          }}
                        >
                          View Logs
                        </Button>
                        
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Schedule />}
                          onClick={() => {/* TODO: Edit schedule */}}
                        >
                          {t('backoffice.epicorConnect.actions.schedule')}
                        </Button>
                      </Box>

                      <Box display="flex" gap={1}>
                        <IconButton 
                          size="small" 
                          title={t('backoffice.epicorConnect.actions.editJob')}
                          onClick={() => {/* TODO: Edit job */}}
                        >
                          <Edit />
                        </IconButton>
                        
                        <IconButton 
                          size="small" 
                          title={t('backoffice.epicorConnect.actions.jobSettings')}
                          onClick={() => {/* TODO: Job settings */}}
                        >
                          <Settings />
                        </IconButton>
                        
                        <IconButton 
                          size="small" 
                          title={t('backoffice.epicorConnect.actions.deleteJob')} 
                          color="error"
                          onClick={() => {/* TODO: Delete job with confirmation */}}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {dashboard.sync_jobs.length === 0 && (
            <Box textAlign="center" py={4}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('backoffice.epicorConnect.syncJobsManagement.noJobsFound')}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {t('backoffice.epicorConnect.syncJobsManagement.noJobsDescription')}
              </Typography>
              <Button
                variant="contained"
                startIcon={<CloudSync />}
                onClick={() => {/* TODO: Open create job dialog */}}
              >
                {t('backoffice.epicorConnect.actions.createFirstSyncJob')}
              </Button>
            </Box>
          )}
        </TabPanel>

        {/* Execution History Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6">
              {t('backoffice.epicorConnect.executionHistory.title')}
            </Typography>
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => loadDashboard(true)}
                disabled={loading}
              >
                {t('backoffice.epicorConnect.actions.refresh')}
              </Button>
            </Box>
          </Box>

          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="info.main">
                    {dashboard.last_24h_executions}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.last24Hours')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {dashboard.sync_jobs.filter(j => j.status === 'completed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.successfulToday')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {dashboard.sync_jobs.filter(j => j.status === 'failed').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.failedJobs')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">
                    {dashboard.sync_jobs.reduce((sum, job) => sum + job.records_processed, 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.totalRecords')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recent Executions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('backoffice.epicorConnect.executionHistory.recentExecutions')}
              </Typography>
              
              {dashboard.sync_jobs.length > 0 ? (
                <Grid container spacing={2}>
                  {dashboard.sync_jobs
                    .filter(job => job.last_run_at)
                    .sort((a, b) => new Date(b.last_run_at!).getTime() - new Date(a.last_run_at!).getTime())
                    .slice(0, 10)
                    .map((job) => (
                    <Grid size={{ xs: 12 }} key={`${job.job_id}-history`}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box flex={1}>
                            <Typography variant="subtitle1" gutterBottom>
                              {job.job_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {SYNC_JOB_TYPE_LABELS[job.job_type]}
                            </Typography>
                          </Box>
                          
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                {t('backoffice.epicorConnect.jobDetails.records')}
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {job.records_processed.toLocaleString()}
                              </Typography>
                            </Box>
                            
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                {t('backoffice.epicorConnect.jobDetails.successRate')}
                              </Typography>
                              <Typography 
                                variant="body1" 
                                fontWeight="medium"
                                color={job.success_rate >= 90 ? 'success.main' : 'warning.main'}
                              >
                                {epicorConnectService.formatSuccessRate(job.success_rate)}
                              </Typography>
                            </Box>
                            
                            <Box textAlign="center">
                              <Typography variant="body2" color="text.secondary">
                                {t('backoffice.epicorConnect.jobDetails.lastRun')}
                              </Typography>
                              <Typography variant="body1" fontWeight="medium">
                                {epicorConnectService.getLastRunDisplay(job.last_run_at)}
                              </Typography>
                            </Box>
                            
                            <Chip
                              label={SYNC_JOB_STATUS_LABELS[job.status]}
                              size="small"
                              sx={{
                                backgroundColor: SYNC_JOB_STATUS_COLORS[job.status],
                                color: 'white',
                                minWidth: '80px'
                              }}
                            />
                            
                            <IconButton 
                              size="small" 
                              title={t('backoffice.epicorConnect.actions.viewDetails')}
                              onClick={() => {
                                setLogsDialog({
                                  open: true,
                                  jobId: job.job_id,
                                  jobName: job.job_name
                                });
                                loadExecutionLogs(job.job_id);
                              }}
                            >
                              <Visibility />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.noHistoryAvailable')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('backoffice.epicorConnect.executionHistory.noHistoryDescription')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </TabPanel>

        {/* Settings Tab */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h6" gutterBottom>
            {t('backoffice.epicorConnect.settings.title')}
          </Typography>

          <Grid container spacing={3}>
            {/* Environment Configuration */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CloudSync color="primary" />
                    <Typography variant="h6">
                      {t('backoffice.epicorConnect.settings.environmentConfiguration')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.environment.environmentName')}
                    </Typography>
                    <Typography variant="body1" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {dashboard.epicor_environment.environment_name}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.environment.baseUrl')}
                    </Typography>
                    <Typography variant="body1" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {dashboard.epicor_environment.base_url}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.environment.databaseCompany')}
                    </Typography>
                    <Typography variant="body1" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {dashboard.epicor_environment.database_company}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.environment.apiVersion')}
                    </Typography>
                    <Typography variant="body1" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                      {dashboard.epicor_environment.api_version}
                    </Typography>
                  </Box>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {t('backoffice.epicorConnect.environment.connectionStatus')}
                      </Typography>
                      <Chip
                        icon={getConnectionStatusIcon(dashboard.epicor_environment.connection_status)}
                        label={dashboard.epicor_environment.connection_status}
                        color={getConnectionStatusColor(dashboard.epicor_environment.connection_status) as any}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CheckCircle />}
                      onClick={() => {/* TODO: Test connection */}}
                    >
                      {t('backoffice.epicorConnect.actions.testConnection')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Sync Job Defaults */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Settings color="primary" />
                    <Typography variant="h6">
                      {t('backoffice.epicorConnect.settings.defaultSyncSettings')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.defaultSchedule')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.defaultScheduleValue')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.batchSizeProducts')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.batchSizeProductsValue')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.pageSizePricing')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.pageSizePricingValue')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.retryAttempts')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.retryAttemptsValue')}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Edit />}
                    onClick={() => {/* TODO: Edit defaults */}}
                  >
                    {t('backoffice.epicorConnect.actions.editDefaults')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* Monitoring & Alerts */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Warning color="primary" />
                    <Typography variant="h6">
                      {t('backoffice.epicorConnect.settings.monitoringAlerts')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.jobFailureNotifications')}
                    </Typography>
                    <Chip label={t('backoffice.epicorConnect.status.enabled')} color="success" size="small" />
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.performanceMonitoring')}
                    </Typography>
                    <Chip label={t('backoffice.epicorConnect.status.enabled')} color="success" size="small" />
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.dataQualityChecks')}
                    </Typography>
                    <Chip label={t('backoffice.epicorConnect.status.enabled')} color="success" size="small" />
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.retentionPeriod')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.retentionPeriodValue')}
                    </Typography>
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Settings />}
                    onClick={() => {/* TODO: Configure monitoring */}}
                  >
                    {t('backoffice.epicorConnect.actions.configureAlerts')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            {/* System Information */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <History color="primary" />
                    <Typography variant="h6">
                      {t('backoffice.epicorConnect.settings.systemInformation')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.moduleVersion')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.moduleVersionValue')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.lastSystemUpdate')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.lastSystemUpdateValue')}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.activeBackgroundJobs')}
                    </Typography>
                    <Typography variant="body1">
                      {t('backoffice.epicorConnect.settings.activeBackgroundJobsValue', { count: dashboard.running_jobs })}
                    </Typography>
                  </Box>
                  
                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('backoffice.epicorConnect.settings.systemHealth')}
                    </Typography>
                    <Chip 
                      label={t('backoffice.epicorConnect.status.healthy')} 
                      color="success" 
                      size="small"
                      icon={<CheckCircle />}
                    />
                  </Box>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Refresh />}
                    onClick={() => {/* TODO: System diagnostics */}}
                  >
                    {t('backoffice.epicorConnect.actions.runDiagnostics')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Execution Logs Dialog */}
      <Dialog
        open={logsDialog.open}
        onClose={() => {
          setLogsDialog({ open: false, jobId: null, jobName: '' });
          setExecutionLogs(null);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <History color="primary" />
            <Typography variant="h6">
              Execution Logs - {logsDialog.jobName}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box p={2}>
            {logsLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
                <Typography variant="body2" sx={{ ml: 2 }}>Loading execution logs...</Typography>
              </Box>
            ) : executionLogs && executionLogs.executions.length > 0 ? (
              <Box>
                <Typography variant="h6" gutterBottom color="primary">
                  Recent Execution History ({executionLogs.total_count} total executions)
                </Typography>
                
                <Grid container spacing={2}>
                  {executionLogs.executions.map((execution, index) => (
                    <Grid size={{ xs: 12 }} key={execution.id}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <Typography variant="subtitle1" fontWeight="medium">
                                Execution #{executionLogs.executions.length - index}
                              </Typography>
                              <Chip
                                label={SYNC_JOB_STATUS_LABELS[execution.status]}
                                size="small"
                                sx={{
                                  backgroundColor: SYNC_JOB_STATUS_COLORS[execution.status],
                                  color: 'white'
                                }}
                              />
                            </Box>
                            
                            <Grid container spacing={2} sx={{ mb: 1 }}>
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="body2" color="text.secondary">Started</Typography>
                                <Typography variant="body2">
                                  {new Date(execution.started_at).toLocaleString()}
                                </Typography>
                              </Grid>
                              
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="body2" color="text.secondary">Duration</Typography>
                                <Typography variant="body2">
                                  {execution.duration_seconds ? epicorConnectService.formatDuration(execution.duration_seconds) : 'In progress'}
                                </Typography>
                              </Grid>
                              
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="body2" color="text.secondary">Records Processed</Typography>
                                <Typography variant="body2">{execution.records_processed.toLocaleString()}</Typography>
                              </Grid>
                              
                              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                                <Typography variant="body2" color={execution.success_rate >= 90 ? 'success.main' : 'warning.main'}>
                                  {execution.success_rate.toFixed(1)}%
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="body2" color="text.secondary">Created</Typography>
                                <Typography variant="body2" color="success.main">
                                  {execution.records_created.toLocaleString()}
                                </Typography>
                              </Grid>
                              
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="body2" color="text.secondary">Updated</Typography>
                                <Typography variant="body2" color="info.main">
                                  {execution.records_updated.toLocaleString()}
                                </Typography>
                              </Grid>
                              
                              <Grid size={{ xs: 12, sm: 4 }}>
                                <Typography variant="body2" color="text.secondary">Failed</Typography>
                                <Typography variant="body2" color="error.main">
                                  {execution.records_failed.toLocaleString()}
                                </Typography>
                              </Grid>
                            </Grid>
                            
                            {execution.error_message && (
                              <Box mt={2} p={1} sx={{ bgcolor: 'error.lighter', borderRadius: 1 }}>
                                <Typography variant="body2" color="error.main" fontWeight="medium">
                                  Error Message:
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                  {execution.error_message}
                                </Typography>
                              </Box>
                            )}
                            
                            {(execution.output_log || execution.error_log) && (
                              <Box mt={2}>
                                <Button
                                  size="small"
                                  startIcon={<Terminal fontSize="small" />}
                                  endIcon={expandedLogs.has(execution.id) ? <ExpandLess /> : <ExpandMore />}
                                  onClick={() => toggleLogs(execution.id)}
                                  sx={{ textTransform: 'none' }}
                                >
                                  {expandedLogs.has(execution.id) ? 'Hide' : 'Show'} sync output
                                </Button>

                                <Collapse in={expandedLogs.has(execution.id)} unmountOnExit>
                                  {execution.error_log && (
                                    <Box mt={1}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="error.main" fontWeight={600}>
                                          Error output (stderr)
                                        </Typography>
                                        <Tooltip title="Copy">
                                          <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard?.writeText(execution.error_log || '')}
                                          >
                                            <ContentCopy sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Box
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          p: 1.5,
                                          maxHeight: 320,
                                          overflow: 'auto',
                                          bgcolor: 'grey.900',
                                          color: 'error.light',
                                          borderRadius: 1,
                                          fontSize: '0.75rem',
                                          fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {execution.error_log}
                                      </Box>
                                    </Box>
                                  )}

                                  {execution.output_log && (
                                    <Box mt={1}>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                          Sync output (stdout)
                                        </Typography>
                                        <Tooltip title="Copy">
                                          <IconButton
                                            size="small"
                                            onClick={() => navigator.clipboard?.writeText(execution.output_log || '')}
                                          >
                                            <ContentCopy sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                      <Box
                                        component="pre"
                                        sx={{
                                          m: 0,
                                          p: 1.5,
                                          maxHeight: 320,
                                          overflow: 'auto',
                                          bgcolor: 'grey.900',
                                          color: 'grey.100',
                                          borderRadius: 1,
                                          fontSize: '0.75rem',
                                          fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word'
                                        }}
                                      >
                                        {execution.output_log}
                                      </Box>
                                    </Box>
                                  )}
                                </Collapse>
                              </Box>
                            )}

                            <Box mt={1} display="flex" gap={2}>
                              <Typography variant="caption" color="text.secondary">
                                Trigger: {execution.trigger_type}
                              </Typography>
                              {execution.throughput > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Throughput: {epicorConnectService.formatThroughput(execution.throughput)}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                
                {executionLogs.has_more && (
                  <Box mt={2} textAlign="center">
                    <Typography variant="body2" color="text.secondary">
                      Showing {executionLogs.executions.length} of {executionLogs.total_count} executions
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : logsDialog.jobId ? (
              <Box textAlign="center" py={4}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No execution history found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This sync job has not been executed yet or execution logs are not available.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No job selected
              </Typography>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              setLogsDialog({ open: false, jobId: null, jobName: '' });
              setExecutionLogs(null);
            }}
          >
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Refresh />}
            onClick={() => {
              if (logsDialog.jobId) {
                loadExecutionLogs(logsDialog.jobId);
              }
            }}
            disabled={logsLoading}
          >
            Refresh Logs
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default EpicorConnectPage;